import React, { useMemo, useRef, useState } from "react";
import { unlockedWords } from "../lib/tokiPonaData";
import { ROLES } from "../lib/tokiPonaRoles";
import {
  analyzeSentence,
  targetRole,
  buildSprint,
  sprintWords,
} from "../lib/tokiPonaSprint";
import { Icon, SpeakButton } from "./tokiPonaUi";

// The parse readout: every chip with the hat the parser saw it wearing.
function ParseRow({ analysis }) {
  return (
    <div className="sprint-parse">
      {analysis.roles.map((r, i) => (
        <span className="sprint-parse-tok" key={i}>
          <span className="w">{r.tok}</span>
          {r.role ? (
            <span className={`role-badge role-${r.role}`}>
              {ROLES[r.role].label}
            </span>
          ) : (
            <span className="role-badge role-particle">particle</span>
          )}
        </span>
      ))}
    </div>
  );
}

export default function TokiPonaSprint({ completed, onAward }) {
  // Same pool as the other modes: only words unlocked through lessons. The
  // home screen gates this mode until units 1-2 are done.
  const available = useMemo(() => unlockedWords(completed || {}), [completed]);
  const poolSize = useMemo(() => sprintWords(available).length, [available]);

  const [deal, setDeal] = useState(null); // { word, hats, particles, helpers }
  const [hatIndex, setHatIndex] = useState(0);
  const [tiles, setTiles] = useState([]); // [{ id, tok }]
  const [result, setResult] = useState(null); // { kind: "error"|"parsed", ... }
  const [finished, setFinished] = useState(false);
  const nextId = useRef(1);

  const start = () => {
    setDeal(buildSprint(available));
    setHatIndex(0);
    setTiles([]);
    setResult(null);
    setFinished(false);
  };

  const add = (tok) => {
    setTiles((ts) => [...ts, { id: nextId.current++, tok }]);
    setResult(null);
  };
  const remove = (id) => {
    setTiles((ts) => ts.filter((t) => t.id !== id));
    setResult(null);
  };
  const clear = () => {
    setTiles([]);
    setResult(null);
  };

  const hat = deal && deal.hats[hatIndex];
  const sentence = tiles.map((t) => t.tok);

  const check = () => {
    const analysis = analyzeSentence(sentence);
    if (!analysis.ok) {
      setResult({ kind: "error", msg: analysis.error });
      return;
    }
    const tgt = targetRole(analysis, deal.word);
    if (tgt.found === 0) {
      setResult({
        kind: "error",
        msg: `the sentence needs ${deal.word} in it — that's the word we're dressing`,
      });
      return;
    }
    if (tgt.found > 1) {
      setResult({
        kind: "error",
        msg: `use ${deal.word} exactly once so we can see which hat it wears`,
      });
      return;
    }
    setResult({ kind: "parsed", analysis, actual: tgt.role, hit: tgt.role === hat });
  };

  const advance = () => {
    if (hatIndex + 1 >= deal.hats.length) {
      if (onAward) onAward(deal.hats.length + 2);
      setFinished(true);
    } else {
      setHatIndex(hatIndex + 1);
      setTiles([]);
      setResult(null);
    }
  };

  // ---- intro ---------------------------------------------------------------
  if (!deal) {
    return (
      <div className="sprint-lab">
        <div className="roles-intro">
          <h2>one word, every hat</h2>
          <p>
            The roulette asked you to <i>spot</i> the hat — here you make the
            word wear it. You get one word and a shelf of chips: build a real
            sentence that uses the word as a noun, then a verb, then a
            modifier. Nonsense sentences are fine as long as the grammar
            holds — <i>kili li lukin e mi</i> is a perfectly good day at the
            office.
          </p>
          <button
            className="btn roles-start"
            onClick={start}
            disabled={poolSize < 2}
          >
            o pali! · deal a word
          </button>
          <div className="roles-poolnote">
            drawing from {poolSize} of your words
          </div>
        </div>
      </div>
    );
  }

  // ---- done ----------------------------------------------------------------
  if (finished) {
    return (
      <div className="sprint-lab">
        <div className="roles-intro">
          <div className="trophy">
            <Icon name="trophy" />
          </div>
          <h2>nimi li jo e len ale!</h2>
          <p>
            <b className="sprint-word-inline">{deal.word}</b> wore all{" "}
            {deal.hats.length} of its hats — and you did the dressing.
          </p>
          <button className="btn roles-start" onClick={start}>
            o sin! · new word
          </button>
        </div>
      </div>
    );
  }

  // ---- building ------------------------------------------------------------
  return (
    <div className="sprint-lab">
      <div className="sprint-hats">
        {deal.hats.map((h, i) => {
          let cls = `role-badge role-${h}`;
          if (i < hatIndex) cls += " done";
          else if (i === hatIndex) cls += " current";
          else cls += " todo";
          return (
            <span className={cls} key={h}>
              {i < hatIndex ? "✓ " : ""}
              {ROLES[h].label}
            </span>
          );
        })}
      </div>

      <div className="sprint-prompt">
        <div className="sprint-word">
          {deal.word}
          <SpeakButton text={deal.word} />
        </div>
        <div className="sprint-ask">
          build a sentence where it's a{" "}
          <span className={`role-badge role-${hat}`}>{ROLES[hat].label}</span>
        </div>
      </div>

      <div className="build-answer sprint-row">
        {tiles.length === 0 && (
          <span className="build-hint">tap chips below to build…</span>
        )}
        {tiles.map((t) => (
          <button key={t.id} className="tok" onClick={() => remove(t.id)}>
            {t.tok}
          </button>
        ))}
      </div>

      <div className="sprint-tools">
        <button
          className="sprint-clear"
          onClick={clear}
          disabled={tiles.length === 0}
        >
          clear
        </button>
        <button
          className="btn sprint-check"
          onClick={check}
          disabled={tiles.length === 0 || result !== null}
        >
          check the fit
        </button>
      </div>

      {result && result.kind === "error" && (
        <div className="sprint-note">{result.msg}</div>
      )}

      {result && result.kind === "parsed" && (
        <div className={`foot sprint-foot ${result.hit ? "right" : "wrong"}`}>
          <div className="verdict">
            <b>{result.hit ? "pona a!" : "ike a"}</b>
            <small>
              {result.hit ? (
                <>
                  {deal.word} is wearing the {ROLES[hat].label} hat
                  <SpeakButton text={sentence.join(" ")} />
                </>
              ) : (
                `grammatical — but here ${deal.word} wears the ${
                  ROLES[result.actual].label
                } hat. Move it and try again.`
              )}
            </small>
          </div>
          <ParseRow analysis={result.analysis} />
          {result.hit && (
            <button className="btn" onClick={advance}>
              {hatIndex + 1 >= deal.hats.length ? "finish" : "next hat"}
            </button>
          )}
        </div>
      )}

      <div className="compose-bank sprint-bank">
        <div className="compose-group">
          <div className="lbl">the word</div>
          <div className="toks">
            <button className="tok sprint-target" onClick={() => add(deal.word)}>
              {deal.word}
            </button>
          </div>
        </div>
        <div className="compose-group">
          <div className="lbl">little words</div>
          <div className="toks">
            {deal.particles.map((p) => (
              <button key={p} className="tok particle" onClick={() => add(p)}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="compose-group">
          <div className="lbl">helpers</div>
          <div className="toks">
            {deal.helpers.map((w) => (
              <button key={w} className="tok" onClick={() => add(w)}>
                {w}
              </button>
            ))}
          </div>
        </div>
        <button className="sprint-skip" onClick={start}>
          this word and I aren't getting along — deal another
        </button>
      </div>
    </div>
  );
}
